import React, { useRef, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { StatusChip } from "@/components/StatusChip";

// Readable labels for Stripe Connect requirement codes (raw code is always shown underneath).
const REQ_LABELS = {
  external_account: "Bank account for payouts",
  business_type: "Business type",
  "business_profile.url": "Business website",
  "business_profile.mcc": "Business category",
  "business_profile.product_description": "Product description",
  "business_profile.support_phone": "Support phone",
  "tos_acceptance.date": "Accept Stripe terms",
  "tos_acceptance.ip": "Accept Stripe terms",
  "settings.payments.statement_descriptor": "Statement descriptor",
  "company.name": "Company legal name",
  "company.tax_id": "Company tax ID (EIN)",
  "company.phone": "Company phone",
  "company.owners_provided": "Confirm company owners",
  "company.directors_provided": "Confirm company directors",
  "company.executives_provided": "Confirm company executives",
  "verification.document": "ID document",
  "verification.additional_document": "Additional ID document (proof of address)",
  id_number: "SSN / tax ID number",
  ssn_last_4: "SSN (last 4)",
  first_name: "Legal first name",
  last_name: "Legal last name",
  email: "Email address",
  phone: "Phone number",
  "relationship.title": "Job title",
};
const REQ_PREFIX = [
  [/\.dob\.(day|month|year)$|^dob\./, "Date of birth"],
  [/(^|\.)address\./, "Address"],
  [/^company\.verification\.document/, "Company verification document"],
];
const PERSON = /^(individual|representative|owners|directors|executives|person_[A-Za-z0-9]+)\./;
const WHO = { individual: "", representative: "Representative · ", owners: "Owner · ", directors: "Director · ", executives: "Executive · " };

export function reqLabel(code = "") {
  if (REQ_LABELS[code]) return REQ_LABELS[code];
  const m = code.match(PERSON);
  const who = m ? (WHO[m[1]] ?? "Person · ") : "";
  const rest = m ? code.slice(m[0].length) : code;
  if (REQ_LABELS[rest]) return who + REQ_LABELS[rest];
  const hit = REQ_PREFIX.find(([rx]) => rx.test(code));
  if (hit) return (code.startsWith("company.") ? "Company " + hit[1].toLowerCase() : who + hit[1]);
  return rest.replace(/_/g, " ").replace(/\./g, " · ").replace(/^\w/, (c) => c.toUpperCase());
}

const DISABLED_COPY = {
  "requirements.past_due": "Stripe needs more information (past due)",
  "requirements.pending_verification": "Stripe is verifying submitted details",
  listed: "On a prohibited-business list — under review",
  platform_paused: "Payouts paused by the platform",
  "rejected.fraud": "Rejected — fraud risk",
  "rejected.listed": "Rejected — prohibited business",
  "rejected.terms_of_service": "Rejected — terms of service",
  "rejected.other": "Rejected by Stripe",
  under_review: "Stripe is reviewing this account",
  other: "Disabled by Stripe",
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

function Flag({ label, on }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={on ? "text-olive-bright" : "text-[#E8C46A]"}>{on ? "Yes" : "No"}</span>
    </div>
  );
}

function PayoutDetail({ p, username }) {
  if (p.state === "not_connected") {
    return <p className="text-muted-foreground" data-testid={`payout-detail-none-${username}`}>No Stripe Connect account yet — this member hasn't started payout onboarding.</p>;
  }
  return (
    <div className="space-y-2.5" data-testid={`payout-detail-${username}`}>
      <div className="space-y-1">
        <Flag label="Payouts enabled" on={p.payouts_enabled} />
        <Flag label="Charges enabled" on={p.charges_enabled} />
        <Flag label="Details submitted" on={p.details_submitted} />
      </div>
      {p.reconnect_required && <p className="text-blaze">Stored account is orphaned — member must reconnect.</p>}
      <div>
        <div className="uppercase text-[9px] tracking-[0.12em] text-muted-foreground mb-1">Disabled reason</div>
        {p.disabled_reason ? (
          <div data-testid={`payout-disabled-reason-${username}`}>
            <div className="text-foreground">{DISABLED_COPY[p.disabled_reason] || reqLabel(p.disabled_reason)}</div>
            <div className="text-[10px] text-muted-foreground">{p.disabled_reason}</div>
          </div>
        ) : <div className="text-muted-foreground">None</div>}
      </div>
      <div>
        <div className="uppercase text-[9px] tracking-[0.12em] text-muted-foreground mb-1">Currently due ({p.currently_due.length})</div>
        {p.currently_due.length ? (
          <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid={`payout-currently-due-${username}`}>
            {p.currently_due.map((c) => (
              <li key={c}><div className="text-foreground">{reqLabel(c)}</div><div className="text-[10px] text-muted-foreground break-all">{c}</div></li>
            ))}
          </ul>
        ) : <div className="text-muted-foreground">Nothing due</div>}
      </div>
      <div className="text-[10px] text-muted-foreground border-t border-sniper pt-2">
        Last webhook sync: {p.last_synced_at ? fmt(p.last_synced_at) : "never"}
      </div>
    </div>
  );
}

// Compact PAYOUT badge for the admin roster. Hover previews the Stripe detail; click pins it open.
export function PayoutChip({ payout, username }) {
  const p = payout || { state: "not_connected", currently_due: [] };
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const t = useRef(null);
  const hover = (v) => { clearTimeout(t.current); if (pinned) return; t.current = setTimeout(() => setOpen(v), v ? 120 : 150); };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPinned(false); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`account-payout-chip-${username}`}
          onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}
          onClick={(e) => { e.preventDefault(); clearTimeout(t.current); setPinned(!pinned); setOpen(!pinned); }}
          className="cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-olive">
          <StatusChip state={`payout_${p.state}`} testId={`account-payout-state-${username}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 font-mono text-[11px] bg-surface border-sniper rounded-none"
        onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)} data-testid={`payout-popover-${username}`}>
        <div className="uppercase text-[10px] tracking-[0.12em] text-foreground mb-2">Stripe payout status</div>
        <PayoutDetail p={p} username={username} />
      </PopoverContent>
    </Popover>
  );
}
